import React, { useState } from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  Chip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Home as HomeIcon, Print as PrintIcon } from '@material-ui/icons'

import {
  useQueryPaginator,
  AddressField,
  DetailTable,
  DateTimeField,
  OrderField,
  PaginatedTable,
  RelatedLinkedField,
  RelatedListValueField,
  ShipmentStatusField,
  ValueField,
} from 'src/components/Table'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'

import { skuit } from 'src/services/skuit'
import { FreightShipment, Tag } from 'src/api/models'
import { ExportProps } from 'src/services/skuit/types'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { PackingSlip } from 'src/modules/client/components/freight/PackingSlip'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  shipment: UseQueryResult<FreightShipment>
}

const Details = (props: DetailsProps) => {
  const { shipment } = props
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { print, renderPrintComponent } = useAsyncPrint()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Card mb={6}>
      {renderPrintComponent()}
      <CardContent>
        <Centered>
          <HomeIcon />
          <Typography variant="h6" gutterBottom>
            {shipment && shipment.data?.name}
          </Typography>
          <Grid container justify="center" spacing={4}>
            {['shipped', 'delivered'].includes(shipment?.status || '') && (
              <Grid item>
                <IconButton size="small" onClick={handleClick}>
                  <PrintIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem
                    onClick={() => {
                      print((props) => (
                        <PackingSlip {...props} guid={shipment.data?.guid as string} />
                      ))
                      handleClose()
                    }}
                  >
                    Print Packing Slip
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
          <DetailTable
            obj={shipment}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              { id: 'order', label: 'Order', renderer: RelatedLinkedField(OrderField) },
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'consignee', label: 'Consignee', renderer: ValueField },
              { id: 'address', label: 'Address', renderer: AddressField },
              { id: 'status', label: 'Status', renderer: ShipmentStatusField },
              { id: 'shippedOn', label: 'Shipped on', renderer: DateTimeField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const ShipmentPage = () => {
  const { guid } = useParams<PathParams>()
  const shipment = useQuery<FreightShipment>(['shipment'], () =>
    skuit.client.freight.shipments.retrieve(guid)
  )

  const attachmentsPaginator = useQueryPaginator(
    'shipment-attachments',
    () => skuit.client.freight.shipments.attachments.list(guid),
    {
      params: { page_size: 4, shipments: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'shipment-units',
    skuit.client.freight.shipments.units.list,
    {
      params: { page_size: 5, shipments: guid },
    }
  )
  const barcodedPaginator = useQueryPaginator(
    'shipment-barcoded-units',
    skuit.client.freight.shipments.barcodedUnits.list,
    {
      params: { page_size: 5, shipments: guid },
    }
  )
  const palletsPaginator = useQueryPaginator(
    'shipment-pallets',
    skuit.client.freight.shipments.pallets.list,
    {
      params: { page_size: 5, shipments: guid },
    }
  )

  return (
    <>
      <Helmet title="Freight Shipment" />

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Freight Shipment
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/freight/shipments">
              Freight Shipments
            </Link>
            <Typography>{shipment && shipment.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              <Details shipment={shipment} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <PaginatedFilePreviewGallery
            title="Attachments"
            paginator={attachmentsPaginator}
            getIdent={(item) => item.guid}
            renderItem={(item) => {
              return (
                <>
                  <GenericPreview url={item.file} />
                  <div>
                    {item.tags.map((tag: Tag) => (
                      <Chip key={tag.slug} label={tag.name} />
                    ))}
                  </div>
                </>
              )
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units"
            fields={[
              { id: 'product', label: 'Product', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'sku', label: 'Sku', renderer: RelatedLinkedField(ValueField, 'sku') },
              {
                id: 'batch',
                label: 'Batch',
                ordering: 'batch__client_uid',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'numUnits', label: 'Number of Units', renderer: ValueField },
              {
                id: 'container',
                label: 'Pallet',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={unitsPaginator}
            exportConfig={{
              exportFields: [
                'shipment_guid',
                'shipment_tracking_number',
                'sku_guid',
                'sku_sku',
                'product_guid',
                'product_name',
                'num_units',
                'pallet_guid',
                'pallet_name',
                'client_pallet_id',
              ],
              exportAPI: (props: ExportProps) =>
                skuit.client.freight.shipments.units.export(props, { shipments: guid }),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units (Barcoded)"
            fields={[
              { id: 'unit', label: 'Name', renderer: RelatedLinkedField(ValueField, 'name') },
              {
                id: 'unit.product',
                label: 'Product',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={barcodedPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Pallets"
            fields={[
              { id: 'pallet', label: 'Pallet', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'pallet.client.name', label: 'Client', renderer: ValueField },
              { id: 'pallet.clientID', label: 'Client ID', renderer: ValueField },
              { id: 'pallet.skus', label: 'SKUs', renderer: RelatedListValueField('sku') },
              { id: 'pallet.path', label: 'Path', renderer: ValueField },
            ]}
            paginator={palletsPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
